// WebSocket client for snapshot agent SESSIONS (the Claude Code pipeline
// in the separate agentContainer project).
//
// Unlike runAgentOverSocket in agentSocket.js, this does not resolve a promise
// per turn: a session outlives the socket, and the socket is a *view* onto a
// conversation the server owns. It can be opened, closed and reopened at any
// point, and `attach` reconnects to a conversation by id — including one this
// browser has never seen, because the agent container keeps the worktree and
// the transcript.

import { getServerUri, getSocketUri } from "./uriUtility";

const ATTACHMENT_UPLOAD_TIMEOUT_MS = 90 * 1000;

/** Upload one bounded file into the temporary assets folder for an agent session. */
export async function uploadSnapshotAgentAttachment(baseId, sessionId, file) {
  const form = new FormData();
  form.append("accessToken", localStorage.getItem("session.accessToken") || "");
  form.append("baseID", String(baseId));
  form.append("sessionId", sessionId);
  form.append("file", file, file.name);

  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timeout = controller
    ? setTimeout(() => controller.abort(), ATTACHMENT_UPLOAD_TIMEOUT_MS)
    : null;

  try {
    const response = await fetch(getServerUri("snapshotEditor/uploadAgentAttachment"), {
      method: "POST",
      body: form,
      ...(controller ? { signal: controller.signal } : {}),
    });
    let payload;
    try {
      payload = await response.json();
    } catch (_err) {
      payload = { error: await response.text().catch(() => "") };
    }
    if (!response.ok) {
      const messages = {
        FILE_EXCEEDS_10MB_LIMIT: "File exceeds the 10 MB limit.",
        SNAPSHOT_ASSETS_ARE_NOT_AVAILABLE: "The flow's asset folder is not available.",
      };
      throw new Error(messages[payload.error] || payload.error || `Upload failed (${response.status}).`);
    }
    if (!payload.attachment?.relativePath) throw new Error("Upload succeeded but returned no file path.");
    return payload.attachment;
  } catch (err) {
    if (err.name === "AbortError") throw new Error("Upload timed out. Check your connection and retry.");
    throw err;
  } finally {
    if (timeout) clearTimeout(timeout);
  }
}

/** Remove a session's temporary attachment folder after the card/session closes. */
export async function cleanupSnapshotAgentAttachments(baseId, sessionId, { keepalive = false } = {}) {
  const payload = {
    accessToken: localStorage.getItem("session.accessToken") || "",
    baseID: String(baseId),
    sessionId,
  };
  const response = await fetch(getServerUri("snapshotEditor/cleanupAgentAttachments"), {
    method: "POST",
    ...(keepalive ? {} : { headers: { "Content-Type": "application/json" } }),
    keepalive,
    body: keepalive ? new URLSearchParams(payload) : JSON.stringify(payload),
  });
  if (!response.ok) {
    const message = await response.text().catch(() => "");
    throw new Error(message || `Attachment cleanup failed (${response.status}).`);
  }
}

/** Remove one uploaded attachment without affecting the rest of the session. */
export async function removeSnapshotAgentAttachment(baseId, sessionId, relativePath) {
  const response = await fetch(getServerUri("snapshotEditor/removeAgentAttachment"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      accessToken: localStorage.getItem("session.accessToken") || "",
      baseID: String(baseId),
      sessionId,
      relativePath,
    }),
  });
  if (!response.ok) {
    let message;
    try { message = (await response.json()).error; } catch (_err) { message = await response.text().catch(() => ""); }
    throw new Error(message || `Attachment removal failed (${response.status}).`);
  }
}

/**
 * Fetch the pipeline's availability, its flow list, and every session that can
 * be continued. Used on mount so the tab can render (and offer to resume) before
 * any socket is opened.
 */
export async function fetchSnapshotAgentFlows(baseId) {
  const res = await fetch(getServerUri("snapshotEditor/listAgentFlows"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      accessToken: localStorage.getItem("session.accessToken"),
      baseID: String(baseId),
    }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `Agent flow request failed (${res.status})`);
  }
  return res.json();
}

/** Discover sessions started outside this browser without probing model availability. */
export async function fetchSnapshotAgentSessions(baseId) {
  const res = await fetch(getServerUri("snapshotEditor/listAgentSessions"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      accessToken: localStorage.getItem("session.accessToken"),
      baseID: String(baseId),
    }),
  });
  if (!res.ok) throw new Error(`Agent session list failed (${res.status})`);
  return res.json();
}

/** Name a session, or clear the name with an empty one. Returns { title }. */
export async function renameSnapshotAgentSession(baseId, sessionId, title) {
  const res = await fetch(getServerUri("snapshotEditor/renameAgentSession"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      accessToken: localStorage.getItem("session.accessToken"),
      baseID: String(baseId),
      sessionId: String(sessionId),
      title: String(title == null ? "" : title),
    }),
  });
  if (!res.ok) throw new Error(`Rename failed (${res.status})`);
  return res.json();
}

/**
 * Let the server name the session from the instruction that opened it: a
 * small model reads the prompt and answers in a few words, the way a chat app
 * names a conversation. Returns { title } — `title` is null when the model
 * declined, and then the session keeps whatever name it had.
 */
export async function nameSnapshotAgentSessionFromPrompt(baseId, sessionId, prompt) {
  const res = await fetch(getServerUri("snapshotEditor/renameAgentSession"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      accessToken: localStorage.getItem("session.accessToken"),
      baseID: String(baseId),
      sessionId: String(sessionId),
      fromPrompt: String(prompt || ""),
    }),
  });
  if (!res.ok) throw new Error(`Rename failed (${res.status})`);
  return res.json();
}

/**
 * A session id the server and the agent CLI will both key on.
 *
 * It goes straight to `claude --session-id`, which requires a real UUID —
 * generating it here means the id exists before the first byte is sent, so the
 * client can address the conversation from the very first frame.
 */
export function newSessionId() {
  if (window.crypto && typeof window.crypto.randomUUID === "function") {
    return window.crypto.randomUUID();
  }
  // Older webviews: RFC-4122 v4 shape from getRandomValues.
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Backoff between reconnect attempts. The list is also the attempt budget: once
 * it runs out the connection reports itself offline rather than retrying
 * forever, because a socket the server refuses (an expired token, say) will
 * never come back on its own and a spinner that never resolves is worse than a
 * button that says Reconnect.
 */
const RECONNECT_MS = [1000, 2000, 4000, 8000, 15000, 30000];
const CONNECT_TIMEOUT_MS = 10000;
const ATTACH_TIMEOUT_MS = 30000;
const STABLE_MS = 30000;
const PING_MS = 25000;
const SILENCE_LIMIT_MS = 75000;

export function connectSnapshotAgentSession({ baseId, action, sessionId, prompt, attachments, flow, model,
  reasoningEffort, onSession, onError, onStatus, onClose }) {
  let socket = null, closed = false, sessionOver = false, started = false;
  let attempt = 0, lastMessageAt = 0, missingProbes = 0, operationId = null;
  let reconnectTimer, connectTimer, attachTimer, stableTimer, probeTimer;
  const pending = [];
  const outbox = new Map();
  const status = (state, detail) => onStatus?.(state, detail || null);
  const clearSocketTimers = () => {
    clearTimeout(connectTimer); clearTimeout(attachTimer); clearTimeout(stableTimer); clearTimeout(probeTimer);
  };
  function dropSocket() {
    clearSocketTimers();
    const old = socket; socket = null;
    if (old) { old.onopen = old.onmessage = old.onerror = old.onclose = null; try { old.close(); } catch (_) { /* closing a stale socket is best-effort */ } }
  }
  const sendRaw = (payload) => {
    if (socket?.readyState !== WebSocket.OPEN) return false;
    try { socket.send(JSON.stringify(payload)); return true; } catch (_) { lost(); return false; }
  };
  function scheduleReconnect() {
    if (closed || sessionOver || reconnectTimer) return;
    if (attempt >= RECONNECT_MS.length) {
      status("offline", { attempt, maxAttempts: RECONNECT_MS.length });
      onError?.(new Error("Connection could not be restored. Press Reconnect; your last request is retained."));
      return;
    }
    const inMs = Math.round(RECONNECT_MS[attempt++] * (0.8 + Math.random() * 0.2));
    status("reconnecting", { attempt, maxAttempts: RECONNECT_MS.length, inMs });
    reconnectTimer = setTimeout(() => { reconnectTimer = null; open(); }, inMs);
  }
  function lost() { dropSocket(); scheduleReconnect(); }
  function confirm(session) {
    clearTimeout(attachTimer);
    status("online");
    if (!stableTimer) stableTimer = setTimeout(() => { attempt = 0; stableTimer = null; }, STABLE_MS);
    if (session) {
      missingProbes = 0;
      operationId = session.operationId || session.turns?.[session.turns.length - 1]?.turnId || operationId;
      for (const turn of session.turns || []) if (turn.accepted !== false) outbox.delete(turn.turnId);
      if (!["running", "unreachable"].includes(session.status)) outbox.delete("stop");
    }
  }
  function handle(msg) {
    if (["snapshot_agent_session_update", "snapshot_agent_session_closed"].includes(msg.type)) {
      if (!msg.session && outbox.size) {
        // Acceptance may have been lost. Replay the same idempotency key.
        for (const payload of outbox.values()) sendRaw(payload);
        return;
      }
      if (!msg.session && ++missingProbes < 3) {
        probeTimer = setTimeout(() => sendRaw({ type: "snapshot_agent_session_attach", baseID: baseId, sessionId }), 2000);
        return;
      }
      confirm(msg.session);
      if (msg.type === "snapshot_agent_session_closed") sessionOver = true;
      onSession?.(msg.session, msg.type === "snapshot_agent_session_closed");
      return;
    }
    if (msg.type === "snapshot_agent_error") {
      if (msg.retryable) { lost(); return; }
      if (msg.requestId) outbox.delete(msg.requestId);
      else outbox.clear();
      onError?.(new Error(msg.error || "Agent session failed"), msg.code || null);
    }
  }
  function open() {
    if (closed || sessionOver) return;
    status("reconnecting", { attempt, maxAttempts: RECONNECT_MS.length });
    let current;
    try { current = socket = new WebSocket(getSocketUri()); } catch (_) { scheduleReconnect(); return; }
    connectTimer = setTimeout(() => { if (socket === current) lost(); }, CONNECT_TIMEOUT_MS);
    current.onopen = () => {
      if (socket !== current) return;
      clearTimeout(connectTimer);
      lastMessageAt = Date.now();
      attachTimer = setTimeout(() => { if (socket === current) lost(); }, ATTACH_TIMEOUT_MS);
      if (!started && action === "start") {
        started = true;
        const requestId = newSessionId();
        outbox.set(requestId, { type: "snapshot_agent_session_start", baseID: baseId, sessionId, requestId, prompt,
          ...(attachments?.length ? { attachments } : {}),
          ...(flow ? { flow } : {}), ...(model ? { model } : {}), ...(reasoningEffort ? { reasoningEffort } : {}) });
      } else sendRaw({ type: "snapshot_agent_session_attach", baseID: baseId, sessionId });
      for (const payload of outbox.values()) sendRaw(payload);
      while (pending.length && socket === current) { if (!sendRaw(pending[0])) break; pending.shift(); }
    };
    current.onmessage = ({ data }) => {
      if (socket !== current) return;
      lastMessageAt = Date.now();
      let msg; try { msg = JSON.parse(data); } catch (_) { return; }
      handle(msg);
    };
    current.onerror = () => { if (socket === current) lost(); };
    current.onclose = () => {
      if (socket !== current) return;
      dropSocket();
      if (closed || sessionOver) onClose?.(); else scheduleReconnect();
    };
  }
  const heartbeat = setInterval(() => {
    if (socket?.readyState !== WebSocket.OPEN) return;
    if (Date.now() - lastMessageAt > SILENCE_LIMIT_MS) { lost(); return; }
    sendRaw({ type: "ping" });
  }, PING_MS);
  const post = (payload, key) => {
    if (key) outbox.set(key, payload);
    if (!sendRaw(payload) && !key) pending.push(payload);
    if (!socket && !reconnectTimer && !closed) { attempt = 0; open(); }
  };
  open();
  return {
    send(nextPrompt, nextAttachments = []) {
      const requestId = newSessionId();
      post({ type: "snapshot_agent_session_message", baseID: baseId, sessionId, requestId,
        prompt: nextPrompt, ...(nextAttachments.length ? { attachments: nextAttachments } : {}), ...(model ? { model } : {}) }, requestId);
    },
    stop() {
      post({ type: "snapshot_agent_session_stop", baseID: baseId, sessionId, operationId }, "stop");
      if (!socket && !reconnectTimer) { attempt = 0; open(); }
    },
    startNow() { post({ type: "snapshot_agent_session_start_now", baseID: baseId, sessionId }); },
    end() { post({ type: "snapshot_agent_session_end", baseID: baseId, sessionId }); },
    discard() { post({ type: "snapshot_agent_session_discard", baseID: baseId, sessionId }); },
    reconnect() {
      if (closed || sessionOver) return;
      clearTimeout(reconnectTimer); reconnectTimer = null;
      dropSocket(); attempt = 0; missingProbes = 0; open();
    },
    hasPending() { return pending.length > 0 || outbox.size > 0; },
    close() {
      closed = true; clearInterval(heartbeat); clearTimeout(reconnectTimer); reconnectTimer = null;
      outbox.clear(); pending.length = 0; dropSocket();
    },
  };
}
